// src/components/ConfiguratorMain.jsx

import React, { useMemo, useState, useEffect, Suspense, useRef } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment, useGLTF } from "@react-three/drei";

// -------------------- Local pattern thumbs --------------------
import design1 from "../../images/1st design.png";
import design2 from "../../images/4th design.png";
import design3 from "../../images/5rd Design.png";
import design4 from "../../images/9rd Design.png";
import design5 from "../../images/13rd Design.png";

// -------------------- Local logo thumbs --------------------
import logo1 from "../../images/p-logo1.png";


// -------------------- SAMPLE DESIGN DATA --------------------
const ONLINE_DESIGNS = [
  { id: 1, name: "Stripes A", img: "https://images.unsplash.com/photo-1526401485004-2fda9f4d7d88?q=80&w=800&auto=format&fit=crop" },
  { id: 2, name: "Diagonal A", img: "https://images.unsplash.com/photo-1520975916090-3105956dac38?q=80&w=800&auto=format&fit=crop" },
  { id: 3, name: "Brush A",   img: "https://images.unsplash.com/photo-1503341504253-dff4815485f1?q=80&w=800&auto=format&fit=crop" },
  { id: 4, name: "Blocks A",  img: "https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=800&auto=format&fit=crop" },
  { id: 5, name: "Lines A",   img: "https://images.unsplash.com/photo-1520974735194-5f54d49d35f4?q=80&w=800&auto=format&fit=crop" },
  { id: 6, name: "Bold A",    img: "https://images.unsplash.com/photo-1549577408-b7bcae8945b9?q=80&w=800&auto=format&fit=crop" },
  { id: 7, name: "Center A",  img: "https://images.unsplash.com/photo-1531685250784-7569952593d2?q=80&w=800&auto=format&fit=crop" },
  { id: 8, name: "Panel A",   img: "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?q=80&w=800&auto=format&fit=crop" },
  { id: 9, name: "Classic A", img: "https://images.unsplash.com/photo-1502877338535-766e1452684a?q=80&w=800&auto=format&fit=crop" },
];

// -------------------- PATTERN DATA --------------------
const PATTERNS = [
  { id: 1, name: "Zig Zag",     img: design1 },
  { id: 2, name: "Polka Dots",  img: design2 },
  { id: 3, name: "Geometric",   img: design3 },
  { id: 4, name: "Camouflage",  img: design3 },
  { id: 5, name: "Floral",      img: design4 },
  { id: 6, name: "Stripes",     img: design5 },
];

// -------------------- LOGO DATA (6 built-in logos) --------------------
const LOGOS = [
  { id: 1, name: "Nike", img: logo1 },
];

// -------------------- helpers to manage uniforms --------------------
function setUniformColor(uniform, colorTHREE) {
  if (!uniform) return;
  const v = uniform.value;
  if (v && v.isColor) v.copy(colorTHREE);
  else if (v && v.isVector3) v.set(colorTHREE.r, colorTHREE.g, colorTHREE.b);
  else uniform.value = colorTHREE.clone();
}
function setUniformVec2(uniform, vec2THREE) {
  if (!uniform) return;
  const v = uniform.value;
  if (v && v.isVector2) v.copy(vec2THREE);
  else if (Array.isArray(v) && v.length >= 2) { v[0] = vec2THREE.x; v[1] = vec2THREE.y; }
  else uniform.value = vec2THREE.clone();
}
function setUniformFloat(uniform, n) {
  if (!uniform) return;
  uniform.value = Number(n);
}
function setUniformVec3(uniform, vec3THREE) {
  if (!uniform) return;
  const v = uniform.value;
  if (v && v.isVector3) v.copy(vec3THREE);
  else uniform.value = vec3THREE.clone();
}

// -------------------- TEXT / NUMBER CANVAS TEXTURES --------------------
function useTextTexture(
  text,
  {
    font = "Inter, Arial, sans-serif",
    fontSize = 80,
    fill = "#ffffff",
    stroke = "#000000",
    strokeWidth = 0,
    paddingX = 64,
    paddingY = 32,
    textAlign = "center",
    baseline = "middle",
    bg = "transparent",
    isBold = true,
  } = {}
) {
  return useMemo(() => {
    if (!text) return null;

    const scale = 2; // HiDPI
    const measCtx = document.createElement("canvas").getContext("2d");
    measCtx.font = `${isBold ? "bold " : ""}${fontSize * scale}px ${font}`;
    const metrics = measCtx.measureText(text);
    const w = Math.ceil(metrics.width) + paddingX * scale * 2;
    const h = Math.ceil(fontSize * scale) + paddingY * scale * 2;

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(2, w);
    canvas.height = Math.max(2, h);
    const ctx = canvas.getContext("2d");

    if (bg !== "transparent") {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.font = `${isBold ? "bold " : ""}${fontSize * scale}px ${font}`;
    ctx.textAlign = textAlign;
    ctx.textBaseline = baseline;

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    if (strokeWidth > 0) {
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.lineWidth = strokeWidth * scale;
      ctx.strokeStyle = stroke;
      ctx.strokeText(text, cx, cy);
    }

    ctx.fillStyle = fill;
    ctx.fillText(text, cx, cy);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.flipY = false; // IMPORTANT: keep canvas text upright in UV space
    texture.needsUpdate = true;
    return texture;
  }, [text, font, fontSize, fill, stroke, strokeWidth, paddingX, paddingY, textAlign, baseline, bg, isBold]);
}

// -------------------- SAFE OPTIONAL TEXTURE LOADER (for pattern/logo) --------------------
function useOptionalTexture(url, { repeat = true, flipY = true } = {}) {
  const [tex, setTex] = useState(null);
  useEffect(() => {
    if (!url) { setTex(null); return; }
    let alive = true;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    loader.load(
      url,
      (t) => {
        if (!alive) return;
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = 8;
        t.minFilter = THREE.LinearMipmapLinearFilter;
        t.magFilter = THREE.LinearFilter;
        t.generateMipmaps = true;
        t.flipY = flipY;
        if (repeat) {
          t.wrapS = THREE.RepeatWrapping;
          t.wrapT = THREE.RepeatWrapping;
        } else {
          t.wrapS = THREE.ClampToEdgeWrapping;
          t.wrapT = THREE.ClampToEdgeWrapping;
        }
        t.needsUpdate = true;
        setTex(t);
      },
      undefined,
      () => setTex(null)
    );
    return () => { alive = false; };
  }, [url, repeat, flipY]);
  return tex;
}

// -------------------- UTIL --------------------
const makeStop = (color, pos) => ({
  id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2),
  color, pos
});

// -------------------- Mesh name → part key --------------------
const NAME_TO_KEY = {
  // exact names from your screenshot
  "F Shirt": "front",
  "B Shirt": "back",
  "L Shoulder": "lShoulder",
  "R Shoulder": "rShoulder",
  "L Stripe": "lArmStripe",
  "R Stripe": "rArmStripe",
  "Neck Collar": "neck",
  "Short": "shorts",

  // common fallbacks
  Body: "front",
  Torso: "front",
  Shirt: "front",
  TShirt: "front",
  Front: "front",
  Back: "back",
  Shorts: "shorts",
  Collar: "neck",
  Neck: "neck",
  NeckBand: "neck",
  Rib_Collar: "neck",
  LeftSleeve: "lSleeve",
  RightSleeve: "rSleeve",
  LeftArm: "lSleeve",
  RightArm: "rSleeve",
  Sleeve_L: "lSleeve",
  Sleeve_R: "rSleeve",
};

function partKeyFromName(name = "") {
  if (NAME_TO_KEY[name]) return NAME_TO_KEY[name];

  const raw = String(name).toLowerCase();
  const tight = raw.replace(/[^a-z0-9]/g, ""); // e.g. "b shirt" -> "bshirt"

  const has = (s) => tight.includes(s);

  // front/back shirt variants
  if (has("frontshirt") || has("fshirt") || has("frontbody") || has("front") || has("chest")) return "front";
  if (has("backshirt")  || has("bshirt") || has("backbody")  || has("back"))  return "back";

  // shorts (singular/plural)
  if (has("shorts") || has("short") || has("pant") || has("lower")) return "shorts";

  // stripes seen in your file: "lstripe"/"rstripe"
  if (has("larmstripe") || has("leftarmstripe") || has("lstripe")) return "lArmStripe";
  if (has("rarmstripe") || has("rightarmstripe")|| has("rstripe")) return "rArmStripe";

  // shoulders
  if (has("lshoulder")  || has("leftshoulder"))  return "lShoulder";
  if (has("rshoulder")  || has("rightshoulder")) return "rShoulder";

  // sleeves (if present)
  if (has("lsleeve") || (has("left") && has("sleeve")))  return "lSleeve";
  if (has("rsleeve") || (has("right") && has("sleeve"))) return "rSleeve";

  // neck/collar
  if (has("neckstripe") || has("neckband") || has("collar") || has("neck")) return "neck";

  // generic shirt fallback → front
  if (has("shirt") || has("torso") || has("body")) return "front";

  return "other";
}


// --------------------------------------------------------------
//                       SOCCER KIT MODEL
// --------------------------------------------------------------
function SoccerKitModel({
  baseColor,

  useBaseGrad, gradStops, gradAngle,

  // PATTERN
  patternUrl, showPattern, patternColor, patternRepeat = [2.5, 2.5], patternStrength = 0.7,

  // LOGO 1 (Shirt)
  logoUrl, showLogo, logoColor, logoPosition = [0.5, 0.5], logoScale = [0.3, 0.3],
  // LOGO 2 (Shorts)
  logo2Url, showLogo2, logo2Color, logo2Position = [0.6, 0.3], logo2Scale = [0.1, 0.1],

  // Front Text (FRONT ONLY)
  textContent, showText, textColor, textPosition = [0.5, 0.5], textScale = [0.25, 0.25], textFontSize = 80,

  // Back Text (Name) (BACK ONLY)
  backTextContent, showBackText, backTextColor, backTextPosition = [0.5, 0.5], backTextScale = [0.25, 0.25], backTextFontSize = 80,

  // Numbers (BACK ONLY)
  frontNumberValue, showFrontNumber, frontNumberColor, frontNumberPosition = [0.5, 0.5], frontNumberScale = [0.32, 0.32], frontNumberFontSize = 150,
  backNumberValue,  showBackNumber,  backNumberColor,  backNumberPosition  = [0.5, 0.5], backNumberScale  = [0.46, 0.46], backNumberFontSize  = 190,

  // Per-part tint - INCLUDING SLEEVES AND SHORTS
  tintFront, tintBack, tintLArmStripe, tintRArmStripe, tintLShoulder, tintRShoulder, tintNeck,
  tintLSleeve, tintRSleeve, tintShorts,
}) {
  const { scene } = useGLTF("/assets/images/Full Soccer Kit (1).glb");

  // ---------- World gradient bbox ----------
  const [bboxMin, setBboxMin] = useState(new THREE.Vector3(-0.5, -0.5, -0.5));
  const [bboxMax, setBboxMax] = useState(new THREE.Vector3( 0.5,  0.5,  0.5));

  useEffect(() => {
    if (!scene) return;
    scene.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(scene);
    if (!box.isEmpty()) {
      setBboxMin(box.min.clone());
      setBboxMax(box.max.clone());
    }
  }, [scene]);

  // Textures
  const patternTex = useOptionalTexture(showPattern ? patternUrl : null, { repeat: true,  flipY: true });
  const logoTex    = useOptionalTexture(showLogo ? logoUrl : null,       { repeat: false, flipY: true });
  const logo2Tex   = useOptionalTexture(showLogo2 ? logo2Url : null,     { repeat: false, flipY: true });

  // Textures for text & numbers
  const textTex      = useTextTexture(textContent,     { fill: textColor,     strokeWidth: 2, fontSize: textFontSize,     isBold: true });
  const backTextTex  = useTextTexture(backTextContent, { fill: backTextColor, strokeWidth: 0, fontSize: backTextFontSize, isBold: true });
  const frontNumTex  = useTextTexture(frontNumberValue,{ fill: frontNumberColor, strokeWidth: 0, fontSize: frontNumberFontSize, isBold: true });
  const backNumTex   = useTextTexture(backNumberValue, { fill: backNumberColor,  strokeWidth: 0, fontSize: backNumberFontSize,  isBold: true });

  // Compile & inject shaders + clone materials
  useEffect(() => {
    if (!scene) return;

    // unique material per mesh
    scene.traverse((obj) => {
      if (!obj.isMesh || !obj.material) return;
      if (Array.isArray(obj.material)) {
        obj.material = obj.material.map((m) => {
          const std = (m instanceof THREE.MeshStandardMaterial) ? m : new THREE.MeshStandardMaterial().copy(m);
          return std.clone();
        });
      } else {
        const m = obj.material;
        const std = (m instanceof THREE.MeshStandardMaterial) ? m : new THREE.MeshStandardMaterial().copy(m);
        obj.material = std.clone();
      }
    });

    const base = new THREE.Color(baseColor);
    const pCol = new THREE.Color(patternColor);
    const lCol = new THREE.Color(logoColor);
    const l2Col= new THREE.Color(logo2Color);
    const tCol = new THREE.Color(textColor);
    const btCol= new THREE.Color(backTextColor);
    const fnCol= new THREE.Color(frontNumberColor);
    const bnCol= new THREE.Color(backNumberColor);

    const rep  = new THREE.Vector2(patternRepeat[0], patternRepeat[1]);
    const lPos = new THREE.Vector2(logoPosition[0], logoPosition[1]);
    const lScl = new THREE.Vector2(logoScale[0], logoScale[1]);
    const l2Pos= new THREE.Vector2(logo2Position[0], logo2Position[1]);
    const l2Scl= new THREE.Vector2(logo2Scale[0], logo2Scale[1]);
    const tPos = new THREE.Vector2(textPosition[0], textPosition[1]);
    const tScl = new THREE.Vector2(textScale[0], textScale[1]);
    const btPos= new THREE.Vector2(backTextPosition[0], backTextPosition[1]);
    const btScl= new THREE.Vector2(backTextScale[0], backTextScale[1]);
    const fnPos= new THREE.Vector2(frontNumberPosition[0], frontNumberPosition[1]);
    const fnScl= new THREE.Vector2(frontNumberScale[0], frontNumberScale[1]);
    const bnPos= new THREE.Vector2(backNumberPosition[0], backNumberPosition[1]);
    const bnScl= new THREE.Vector2(backNumberScale[0], backNumberScale[1]);

    scene.traverse((obj) => {
      if (!obj.isMesh) return;
      const key = partKeyFromName(obj.name);

      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((mat, idx) => {
        if (!mat) return;

        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          const std = new THREE.MeshStandardMaterial();
          std.copy(mat);
          if (Array.isArray(obj.material)) obj.material[idx] = std;
          else obj.material = std;
          mat = std;
        }

        // solid base fallback
        mat.color.copy(base);
        mat.map = null;
        mat.defines = { ...(mat.defines || {}), USE_UV: "" };

        const thisPartCol =
          key === "front" ? new THREE.Color(tintFront) :
          key === "back" ? new THREE.Color(tintBack) :
          key === "shorts" ? new THREE.Color(tintShorts) :
          key === "lArmStripe" ? new THREE.Color(tintLArmStripe) :
          key === "rArmStripe" ? new THREE.Color(tintRArmStripe) :
          key === "lShoulder" ? new THREE.Color(tintLShoulder) :
          key === "rShoulder" ? new THREE.Color(tintRShoulder) :
          key === "lSleeve" ? new THREE.Color(tintLSleeve) :
          key === "rSleeve" ? new THREE.Color(tintRSleeve) :
          key === "neck" ? new THREE.Color(tintNeck) :
          new THREE.Color(baseColor);

        mat.onBeforeCompile = (shader) => {
          const MAX = 8;
          const colorArr = new Array(MAX).fill(0).map(() => new THREE.Color(0xffffff));
          const posArr   = new Float32Array(MAX).fill(0);

          // ---------- GRADIENT UNIFORMS ----------
          shader.uniforms.uUseBaseGrad   = { value: useBaseGrad ? 1.0 : 0.0 };
          shader.uniforms.uGradCount     = { value: 0 };
          shader.uniforms.uGradColors    = { value: colorArr };
          shader.uniforms.uGradPos       = { value: posArr };
          shader.uniforms.uGradAngle     = { value: gradAngle };

          // ---------- WORLD-GRAD UNIFORMS ----------
          shader.uniforms.uUseWorldGradient = { value: 1.0 };
          shader.uniforms.uBBoxMin = { value: bboxMin.clone() };
          shader.uniforms.uBBoxMax = { value: bboxMax.clone() };

          // ---------- PATTERN UNIFORMS ----------
          shader.uniforms.uUsePattern      = { value: (showPattern && patternTex) ? 1.0 : 0.0 };
          shader.uniforms.uPatternMap      = { value: patternTex || new THREE.Texture() };
          shader.uniforms.uPatternColor    = { value: pCol };
          shader.uniforms.uPatternRepeat   = { value: rep };
          shader.uniforms.uPatternStrength = { value: patternStrength };
         shader.uniforms.uAllowPattern    = { value: (key === "front" || key === "back") ? 1.0 : 0.0 };


          // ---------- LOGO 1 (SHIRT ONLY) ----------
          shader.uniforms.uUseLogo       = { value: (showLogo && logoTex) ? 1.0 : 0.0 };
          shader.uniforms.uLogoMap       = { value: logoTex || new THREE.Texture() };
          shader.uniforms.uLogoColor     = { value: lCol };
          shader.uniforms.uLogoPosition  = { value: lPos };
          shader.uniforms.uLogoScale     = { value: lScl };

          // ---------- LOGO 2 (SHORTS ONLY) ----------
          shader.uniforms.uUseLogo2      = { value: (showLogo2 && logo2Tex) ? 1.0 : 0.0 };
          shader.uniforms.uLogo2Map      = { value: logo2Tex || new THREE.Texture() };
          shader.uniforms.uLogo2Color    = { value: l2Col };
          shader.uniforms.uLogo2Position = { value: l2Pos };
          shader.uniforms.uLogo2Scale    = { value: l2Scl };

          // ---------- TEXT ----------
          shader.uniforms.uUseText       = { value: (showText && textTex) ? 1.0 : 0.0 };
          shader.uniforms.uTextMap       = { value: textTex || new THREE.Texture() };
          shader.uniforms.uTextColor     = { value: tCol };
          shader.uniforms.uTextPosition  = { value: tPos };
          shader.uniforms.uTextScale     = { value: tScl };

          // ---------- BACK TEXT ----------
          shader.uniforms.uUseBackText     = { value: (showBackText && backTextTex) ? 1.0 : 0.0 };
          shader.uniforms.uBackTextMap     = { value: backTextTex || new THREE.Texture() };
          shader.uniforms.uBackTextColor   = { value: btCol };
          shader.uniforms.uBackTextPosition= { value: btPos };
          shader.uniforms.uBackTextScale   = { value: btScl };

          // ---------- NUMBERS ----------
          shader.uniforms.uUseFrontNum   = { value: (showFrontNumber && frontNumTex) ? 1.0 : 0.0 };
          shader.uniforms.uFrontNumMap   = { value: frontNumTex || new THREE.Texture() };
          shader.uniforms.uFrontNumColor = { value: fnCol };
          shader.uniforms.uFrontNumPosition = { value: fnPos };
          shader.uniforms.uFrontNumScale = { value: fnScl };

          shader.uniforms.uUseBackNum    = { value: (showBackNumber && backNumTex) ? 1.0 : 0.0 };
          shader.uniforms.uBackNumMap    = { value: backNumTex || new THREE.Texture() };
          shader.uniforms.uBackNumColor  = { value: bnCol };
          shader.uniforms.uBackNumPosition = { value: bnPos };
          shader.uniforms.uBackNumScale  = { value: bnScl };

          // ---------- PART TINT ----------
          shader.uniforms.uPartTint      = { value: thisPartCol };
          shader.uniforms.uPartStrength  = { value: 1.0 };

          // ---------- PART FLAGS ----------
          shader.uniforms.uIsFrontPart   = { value: (key === "front") ? 1.0 : 0.0 };
          shader.uniforms.uIsBackPart    = { value: (key === "back")  ? 1.0 : 0.0 };
          shader.uniforms.uIsShortsPart  = { value: (key === "shorts") ? 1.0 : 0.0 };

          // ---------- VERTEX PATCH ----------
          shader.vertexShader = shader.vertexShader
            .replace(
              'void main() {',
              `
              varying vec3 vWPos;
              void main() {
              `
            )
            .replace(
              '#include <begin_vertex>',
              `
              #include <begin_vertex>
              vWPos = (modelMatrix * vec4( transformed, 1.0 )).xyz;
              `
            );

          // ---------- FRAGMENT PATCH ----------
          shader.fragmentShader = shader.fragmentShader
            .replace(
              "#include <common>",
              `#include <common>
#define MAX_STOPS 8
uniform float uUseBaseGrad;
uniform int   uGradCount;
uniform vec3  uGradColors[MAX_STOPS];
uniform float uGradPos[MAX_STOPS];
uniform float uGradAngle;

uniform float uUseWorldGradient; // 1=world, 0=uv
uniform vec3  uBBoxMin;
uniform vec3  uBBoxMax;
varying vec3  vWPos;

uniform float uUsePattern;
uniform float uAllowPattern;
uniform sampler2D uPatternMap;
uniform vec3  uPatternColor;
uniform vec2  uPatternRepeat;
uniform float uPatternStrength;

uniform float uUseLogo;
uniform sampler2D uLogoMap;
uniform vec3  uLogoColor;
uniform vec2  uLogoPosition;
uniform vec2  uLogoScale;

uniform float uUseLogo2;
uniform sampler2D uLogo2Map;
uniform vec3  uLogo2Color;
uniform vec2  uLogo2Position;
uniform vec2  uLogo2Scale;

uniform float uUseText;
uniform sampler2D uTextMap;
uniform vec3  uTextColor;
uniform vec2  uTextPosition;
uniform vec2  uTextScale;

uniform float uUseBackText;
uniform sampler2D uBackTextMap;
uniform vec3  uBackTextColor;
uniform vec2  uBackTextPosition;
uniform vec2  uBackTextScale;

uniform float uUseFrontNum;
uniform sampler2D uFrontNumMap;
uniform vec3  uFrontNumColor;
uniform vec2  uFrontNumPosition;
uniform vec2  uFrontNumScale;

uniform float uUseBackNum;
uniform sampler2D uBackNumMap;
uniform vec3  uBackNumColor;
uniform vec2  uBackNumPosition;
uniform vec2  uBackNumScale;

uniform vec3  uPartTint;
uniform float uPartStrength;

uniform float uIsFrontPart;
uniform float uIsBackPart;
uniform float uIsShortsPart;

vec2 rotate2d(vec2 p, float a){
  float s = sin(a), c = cos(a);
  return mat2(c, -s, s, c)*p;
}

vec2 gradSpaceUV(vec2 uv){ return uv; }
vec2 gradSpaceWorld(vec3 wpos){
  vec3 size = max(uBBoxMax - uBBoxMin, vec3(0.0001));
  vec3 n = clamp((wpos - uBBoxMin) / size, 0.0, 1.0);
  return n.xy;
}

vec3 gradientEval(vec2 baseUV, vec3 baseWorld){
  vec2 base = (uUseWorldGradient > 0.5) ? gradSpaceWorld(baseWorld) : gradSpaceUV(baseUV);
  vec2 p = rotate2d(base - 0.5, uGradAngle);
  float t = clamp(p.y + 0.5, 0.0, 1.0);
  if(uGradCount <= 0) return vec3(1.0);
  if(uGradCount == 1) return uGradColors[0];
  if(t <= uGradPos[0]) return uGradColors[0];
  if(t >= uGradPos[uGradCount-1]) return uGradColors[uGradCount-1];
  int idx = 0;
  for(int i=0; i<MAX_STOPS-1; i++){
    if(i >= uGradCount-1) break;
    if(t <= uGradPos[i+1]){ idx = i; break; }
  }
  float a = uGradPos[idx];
  float b = uGradPos[idx+1];
  float rt = (b > a) ? clamp((t - a) / (b - a), 0.0, 1.0) : 0.0;
  return mix(uGradColors[idx], uGradColors[idx+1], rt);
}
`
            )
            .replace(
              "#include <map_fragment>",
              `#include <map_fragment>
#ifdef USE_UV
// 1) Base gradient
if(uUseBaseGrad > 0.5){
  diffuseColor.rgb = gradientEval(vUv, vWPos);
}

// 2) Per-part tint
float tintStr = clamp(uPartStrength, 0.0, 1.0);
vec3 tinted   = diffuseColor.rgb * uPartTint;
diffuseColor.rgb = mix(diffuseColor.rgb, tinted, tintStr);

// 3) Pattern only on front/back/shorts
if(uUsePattern > 0.5 && uAllowPattern > 0.5){
  vec2 uvp = vUv * uPatternRepeat;
  vec4 pat = texture2D(uPatternMap, uvp);
  float lum = dot(pat.rgb, vec3(0.299, 0.587, 0.114));
  float mask = clamp(max(1.0 - lum, pat.a), 0.0, 1.0);
  float s = clamp(uPatternStrength, 0.0, 1.0);
  vec3 tintMix = mix(diffuseColor.rgb, uPatternColor, mask);
  diffuseColor.rgb = mix(diffuseColor.rgb, tintMix, s);
}

// 4a) Logo 1 (SHIRT ONLY) — FRONT ONLY, NO MIRROR
if(uUseLogo > 0.5 && (uIsFrontPart > 0.5)){
  vec2 logoUV = (vUv - uLogoPosition) / uLogoScale + vec2(0.5);
  if(all(greaterThanEqual(logoUV, vec2(0.0))) && all(lessThanEqual(logoUV, vec2(1.0)))) {
    vec4 logo = texture2D(uLogoMap, logoUV);
    vec3 logoTint = logo.rgb * uLogoColor;
    diffuseColor.rgb = mix(diffuseColor.rgb, logoTint, logo.a);
  }
}

// 4b) Logo 2 (SHORTS ONLY)
if(uUseLogo2 > 0.5 && uIsShortsPart > 0.5){
  vec2 logoUV2 = (vUv - uLogo2Position) / uLogo2Scale + vec2(0.5);
  logoUV2.x = 1.0 - logoUV2.x; // existing horizontal mirror for shorts
  if(all(greaterThanEqual(logoUV2, vec2(0.0))) && all(lessThanEqual(logoUV2, vec2(1.0)))) {
    vec4 logo2 = texture2D(uLogo2Map, logoUV2);
    vec3 logoTint2 = logo2.rgb * uLogo2Color;
    diffuseColor.rgb = mix(diffuseColor.rgb, logoTint2, logo2.a);
  }
}

// 5) Front Text (FRONT ONLY)
if(uUseText > 0.5 && uIsFrontPart > 0.5){
  vec2 textUV = (vUv - uTextPosition) / uTextScale + vec2(0.5);
  if(all(greaterThanEqual(textUV, vec2(0.0))) && all(lessThanEqual(textUV, vec2(1.0)))) {
    vec4 tex = texture2D(uTextMap, textUV);
    float a = smoothstep(0.15, 0.85, tex.a);
    diffuseColor.rgb = mix(diffuseColor.rgb, uTextColor, a);
  }
}

// 6) Back Name (BACK ONLY)
if(uUseBackText > 0.5 && uIsBackPart > 0.5){
  vec2 bUV = (vUv - uBackTextPosition) / uBackTextScale + vec2(0.5);
  if(all(greaterThanEqual(bUV, vec2(0.0))) && all(lessThanEqual(bUV, vec2(1.0)))) {
    vec4 tex = texture2D(uBackTextMap, bUV);
    float a = smoothstep(0.15, 0.85, tex.a);
    diffuseColor.rgb = mix(diffuseColor.rgb, uBackTextColor, a);
  }
}

// 7a) Front number (if ever enabled) — FIX: flip Y so it’s not upside down
if(uUseFrontNum > 0.5 && uIsFrontPart > 0.5){
  vec2 nfUV = (vUv - uFrontNumPosition) / uFrontNumScale + vec2(0.5);
  nfUV.y = 1.0 - nfUV.y; // <-- vertical flip fix
  if(all(greaterThanEqual(nfUV, vec2(0.0))) && all(lessThanEqual(nfUV, vec2(1.0)))) {
    vec4 tex = texture2D(uFrontNumMap, nfUV);
    float a = smoothstep(0.15, 0.85, tex.a);
    diffuseColor.rgb = mix(diffuseColor.rgb, uFrontNumColor, a);
  }
}

// 7b) Back number — FIX: flip Y so it’s not upside down
if(uUseBackNum > 0.5 && uIsBackPart > 0.5){
  vec2 nbUV = (vUv - uBackNumPosition) / uBackNumScale + vec2(0.5);
  nbUV.y = 1.0 - nbUV.y; // <-- vertical flip fix
  if(all(greaterThanEqual(nbUV, vec2(0.0))) && all(lessThanEqual(nbUV, vec2(1.0)))) {
    vec4 tex = texture2D(uBackNumMap, nbUV);
    float a = smoothstep(0.15, 0.85, tex.a);
    diffuseColor.rgb = mix(diffuseColor.rgb, uBackNumColor, a);
  }
}
#endif`
            );

          // fill gradient stops
          const MAX_STOPS = 8;
          const maxStops = Math.min(MAX_STOPS, (gradStops?.length || 0));
          shader.uniforms.uGradCount.value = maxStops;
          for (let i = 0; i < maxStops; i++) {
            const s = gradStops[i];
            const c = new THREE.Color(s.color || "#ffffff");
            shader.uniforms.uGradColors.value[i].copy(c);
            shader.uniforms.uGradPos.value[i] = (typeof s.pos === "number") ? s.pos : 0;
          }

          mat.userData.shader = shader;
        };

        mat.needsUpdate = true;
      });
    });
  }, [
    scene,
    baseColor,
    useBaseGrad, gradAngle, gradStops,
    showPattern, patternTex, patternColor, patternRepeat, patternStrength,
    showLogo, logoTex, logoColor, logoPosition, logoScale,
    showLogo2, logo2Tex, logo2Color, logo2Position, logo2Scale,
    showText, textTex, textColor, textPosition, textScale, textFontSize,
    showBackText, backTextTex, backTextColor, backTextPosition, backTextScale, backTextFontSize,
    showFrontNumber, frontNumTex, frontNumberColor, frontNumberPosition, frontNumberScale, frontNumberFontSize,
    showBackNumber, backNumTex, backNumberColor, backNumberPosition, backNumberScale, backNumberFontSize,
    tintFront, tintBack, tintLArmStripe, tintRArmStripe, tintLShoulder, tintRShoulder, tintNeck,
    tintLSleeve, tintRSleeve, tintShorts,
    bboxMin, bboxMax
  ]);

  // Live uniform updates
  useEffect(() => {
    if (!scene) return;

    const pCol = new THREE.Color(patternColor);
    const lCol = new THREE.Color(logoColor);
    const l2Col= new THREE.Color(logo2Color);
    const tCol = new THREE.Color(textColor);
    const btCol= new THREE.Color(backTextColor);
    const fnCol= new THREE.Color(frontNumberColor);
    const bnCol= new THREE.Color(backNumberColor);
    const baseCol = new THREE.Color(baseColor);

    const rep  = new THREE.Vector2(patternRepeat[0], patternRepeat[1]);
    const lPos = new THREE.Vector2(logoPosition[0], logoPosition[1]);
    const lScl = new THREE.Vector2(logoScale[0], logoScale[1]);
    const l2Pos= new THREE.Vector2(logo2Position[0], logo2Position[1]);
    const l2Scl= new THREE.Vector2(logo2Scale[0], logo2Scale[1]);
    const tPos = new THREE.Vector2(textPosition[0], textPosition[1]);
    const tScl = new THREE.Vector2(textScale[0], textScale[1]);
    const btPos= new THREE.Vector2(backTextPosition[0], backTextPosition[1]);
    const btScl= new THREE.Vector2(backTextScale[0], backTextScale[1]);
    const fnPos= new THREE.Vector2(frontNumberPosition[0], frontNumberPosition[1]);
    const fnScl= new THREE.Vector2(frontNumberScale[0], frontNumberScale[1]);
    const bnPos= new THREE.Vector2(backNumberPosition[0], backNumberPosition[1]);
    const bnScl= new THREE.Vector2(backNumberScale[0], backNumberScale[1]);

    scene.traverse((obj) => {
      if (!obj.isMesh) return;
      const key = partKeyFromName(obj.name);

      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        if (!m) return;

        if (m.color) m.color.copy(baseCol);

        const sh = m.userData?.shader;
        if (!sh || !sh.uniforms) return;

        // world gradient + bbox
        setUniformFloat(sh.uniforms.uUseBaseGrad, useBaseGrad ? 1.0 : 0.0);
        setUniformFloat(sh.uniforms.uUseWorldGradient, 1.0);
        setUniformVec3(sh.uniforms.uBBoxMin, bboxMin);
        setUniformVec3(sh.uniforms.uBBoxMax, bboxMax);
        setUniformFloat(sh.uniforms.uGradAngle,   gradAngle);

        // Pattern
        setUniformFloat(sh.uniforms.uUsePattern, (showPattern && !!patternUrl) ? 1.0 : 0.0);
        if (sh.uniforms.uPatternMap && patternTex) sh.uniforms.uPatternMap.value = patternTex;
        setUniformColor(sh.uniforms.uPatternColor, pCol);
        setUniformVec2(sh.uniforms.uPatternRepeat, rep);
        setUniformFloat(sh.uniforms.uPatternStrength, patternStrength);

        // Logo 1
        setUniformFloat(sh.uniforms.uUseLogo, (showLogo && !!logoUrl) ? 1.0 : 0.0);
        if (sh.uniforms.uLogoMap && logoTex) sh.uniforms.uLogoMap.value = logoTex;
        setUniformColor(sh.uniforms.uLogoColor, lCol);
        setUniformVec2(sh.uniforms.uLogoPosition, lPos);
        setUniformVec2(sh.uniforms.uLogoScale,    lScl);

        // Logo 2 (shorts)
        setUniformFloat(sh.uniforms.uUseLogo2, (showLogo2 && !!logo2Url) ? 1.0 : 0.0);
        if (sh.uniforms.uLogo2Map && logo2Tex) sh.uniforms.uLogo2Map.value = logo2Tex;
        setUniformColor(sh.uniforms.uLogo2Color, l2Col);
        setUniformVec2(sh.uniforms.uLogo2Position, l2Pos);
        setUniformVec2(sh.uniforms.uLogo2Scale,    l2Scl);

        // Front text
        setUniformFloat(sh.uniforms.uUseText, (showText && !!textContent) ? 1.0 : 0.0);
        if (sh.uniforms.uTextMap && textTex) sh.uniforms.uTextMap.value = textTex;
        setUniformColor(sh.uniforms.uTextColor, tCol);
        setUniformVec2(sh.uniforms.uTextPosition, tPos);
        setUniformVec2(sh.uniforms.uTextScale,    tScl);

        // Back name
        setUniformFloat(sh.uniforms.uUseBackText, (showBackText && !!backTextContent) ? 1.0 : 0.0);
        if (sh.uniforms.uBackTextMap && backTextTex) sh.uniforms.uBackTextMap.value = backTextTex;
        setUniformColor(sh.uniforms.uBackTextColor, btCol);
        setUniformVec2(sh.uniforms.uBackTextPosition, btPos);
        setUniformVec2(sh.uniforms.uBackTextScale,    btScl);

        // Numbers (we keep front num disabled from UI, but uniforms are live)
        setUniformFloat(sh.uniforms.uUseFrontNum, (showFrontNumber && !!frontNumberValue) ? 1.0 : 0.0);
        if (sh.uniforms.uFrontNumMap && frontNumTex) sh.uniforms.uFrontNumMap.value = frontNumTex;
        setUniformColor(sh.uniforms.uFrontNumColor, fnCol);
        setUniformVec2(sh.uniforms.uFrontNumPosition, fnPos);
        setUniformVec2(sh.uniforms.uFrontNumScale,    fnScl);

        setUniformFloat(sh.uniforms.uUseBackNum, (showBackNumber && !!backNumberValue) ? 1.0 : 0.0);
        if (sh.uniforms.uBackNumMap && backNumTex) sh.uniforms.uBackNumMap.value = backNumTex;
        setUniformColor(sh.uniforms.uBackNumColor, bnCol);
        setUniformVec2(sh.uniforms.uBackNumPosition,  bnPos);
        setUniformVec2(sh.uniforms.uBackNumScale,     bnScl);

        // Part tint
        const partCol =
          key === "front" ? tintFront :
          key === "back" ? tintBack :
          key === "shorts" ? tintShorts :
          key === "lArmStripe" ? tintLArmStripe :
          key === "rArmStripe" ? tintRArmStripe :
          key === "lShoulder" ? tintLShoulder :
          key === "rShoulder" ? tintRShoulder :
          key === "lSleeve" ? tintLSleeve :
          key === "rSleeve" ? tintRSleeve :
          key === "neck" ? tintNeck :
          "#ffffff";
        setUniformColor(sh.uniforms.uPartTint, new THREE.Color(partCol));
        setUniformFloat(sh.uniforms.uPartStrength, 1.0);
      });
    });
  }, [
    scene,
    baseColor,
    showPattern, patternTex, patternUrl, patternColor, patternRepeat, patternStrength,
    showLogo, logoTex, logoUrl, logoColor, logoPosition, logoScale,
    showLogo2, logo2Tex, logo2Url, logo2Color, logo2Position, logo2Scale,
    showText, textTex, textContent, textColor, textPosition, textScale,
    showBackText, backTextTex, backTextContent, backTextColor, backTextPosition, backTextScale,
    showFrontNumber, frontNumTex, frontNumberValue, frontNumberColor, frontNumberPosition, frontNumberScale,
    showBackNumber, backNumTex, backNumberValue, backNumberColor, backNumberPosition, backNumberScale,
    tintFront, tintBack, tintLArmStripe, tintRArmStripe, tintLShoulder, tintRShoulder, tintNeck,
    tintLSleeve, tintRSleeve, tintShorts,
    gradAngle, useBaseGrad, bboxMin, bboxMax
  ]);

  if (scene) scene.rotation.set(0, Math.PI, 0);
  return <primitive object={scene} scale={0.3} position={[0, -1.9, 0]} />;
}

function TexturedSoccerKit(props) {
  return (
    <Suspense fallback={null}>
      <SoccerKitModel {...props} />
      <Environment preset="studio" />
    </Suspense>
  );
}

// -------------------- MAIN CONFIGURATOR --------------------
export default function ConfiguratorMain() {
  const [activeTab, setActiveTab]   = useState("Design");
  const [selected, setSelected]     = useState(ONLINE_DESIGNS[6]);

  // Solid base (fallback when gradient OFF)
  const [bgColor] = useState("#ffffff");

  // Pattern controls
  const [patternColor, setPatternColor]   = useState("#1e4ed8");
  const [patternRepeat, setPatternRepeat] = useState({ x: 1, y: 0.1 });
  const [patternStrength, setPatternStrength] = useState(0.7);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [showPattern, setShowPattern]         = useState(false);

  // Logo 1 controls (Shirt)
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [showLogo, setShowLogo]         = useState(false);
  const [logoColor, setLogoColor]       = useState("#ffffff");
  const [logoPosition, setLogoPosition] = useState({ x: 0.4, y: 0.5 });
  const [logoScale, setLogoScale]       = useState({ x: 0.1, y: 0.1 });

  // Logo 2 controls (Shorts)
  const [selectedLogo2, setSelectedLogo2] = useState(null);
  const [showLogo2, setShowLogo2]         = useState(false);
  const [logo2Color, setLogo2Color]       = useState("#ffffff");
  const [logo2Position, setLogo2Position] = useState({x: 0.4, y: 0.5 });
  const [logo2Scale, setLogo2Scale]       = useState({ x: 0.1, y: 0.1 });

  // **Uploaded logo URLs (blob:) + cleanup refs**
  const [userLogo1Url, setUserLogo1Url] = useState(null);
  const [userLogo2Url, setUserLogo2Url] = useState(null);
  const userLogo1Ref = useRef(null);
  const userLogo2Ref = useRef(null);

  // Which logo's settings panel is open?
  const [activeLogoPanel, setActiveLogoPanel] = useState(null);

  // Front Text controls (FRONT ONLY)
  const [textContent, setTextContent]   = useState("");
  const [showText, setShowText]         = useState(false);
  const [textColor, setTextColor]       = useState("#000000");
  const [textPosition, setTextPosition] = useState({ x: 0.40, y: 0.45});
  const [textScale, setTextScale]       = useState({ x:  0.5, y: 0.5 });
  const [textFontSize, setTextFontSize] = useState(20);
  const [autoFitFront, setAutoFitFront] = useState(true);

  // Back Text (Name) (BACK ONLY)
  const [backTextContent, setBackTextContent]   = useState("");
  const [showBackText, setShowBackText]         = useState(false);
  const [backTextColor, setBackTextColor]       = useState("#000000");
  const [backTextPosition, setBackTextPosition] = useState({ x: 0.82, y: 0.50 });
  const [backTextScale, setBackTextScale]       = useState({ x: 0.5, y: 0.5 });
  const [backTextFontSize, setBackTextFontSize] = useState(20);
  const [autoFitBack, setAutoFitBack]           = useState(true);
  const [mirrorBackFromFront, setMirrorBackFromFront] = useState(false);

  // Numbers (BACK ONLY)
  const [frontNumberValue, setFrontNumberValue] = useState("");
  const [showFrontNumber, setShowFrontNumber]   = useState(false);
  const [frontNumberColor, setFrontNumberColor] = useState("#000000");
  const [frontNumberPosition, setFrontNumberPosition] = useState({ x: 0.5, y: 0.5 });
  const [frontNumberScale, setFrontNumberScale] = useState({ x: 0.32, y: 0.32 });
  const [frontNumberFontSize, setFrontNumberFontSize] = useState(150);

  const [backNumberValue, setBackNumberValue]   = useState("");
  const [showBackNumber, setShowBackNumber]     = useState(false);
  const [backNumberColor, setBackNumberColor]   = useState("#000000");
  const [backNumberPosition, setBackNumberPosition] = useState({ x: 0.82, y: 0.40 });
  const [backNumberScale, setBackNumberScale]   = useState({ x: 0.46, y: 0.46 });
  const [backNumberFontSize, setBackNumberFontSize] = useState(30);

  // Multi-stop gradient
  const [useBaseGrad, setUseBaseGrad] = useState(false);
  const [gradStops, setGradStops] = useState([
    makeStop("#1e293b", 0.00),
    makeStop("#0ea5e9", 0.50),
    makeStop("#22c55e", 1.00),
  ]);
  const [baseGradAngleDeg, setBaseGradAngleDeg] = useState(90);

  // Per-part tints - INCLUDING SLEEVES AND SHORTS
  const [tintFront, setTintFront]           = useState("#ffffff");
  const [tintBack, setTintBack]             = useState("#ffffff");
  const [tintLArmStripe, setTintLArmStripe] = useState("#ffffff");
  const [tintRArmStripe, setTintRArmStripe] = useState("#ffffff");
  const [tintLShoulder, setTintLShoulder]   = useState("#ffffff");
  const [tintRShoulder, setTintRShoulder]   = useState("#ffffff");
  const [tintNeck, setTintNeck]             = useState("#ffffff");
  const [tintLSleeve, setTintLSleeve]       = useState("#ffffff");
  const [tintRSleeve, setTintRSleeve]       = useState("#ffffff");
  const [tintShorts, setTintShorts]         = useState("#ffffff");

  // Auto-rotation control
  const [autoRotate, setAutoRotate] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const controlsRef = useRef();

  const previewStyle = useMemo(
    () => ({ backgroundImage: `url("${selected.img}")` }),
    [selected]
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const editingTabs = ["Color", "Pattern", "Gradient", "Logo", "Text", "Name", "Number"];
    const nowEditing = editingTabs.includes(tab);
    setIsEditing(nowEditing);
    setAutoRotate(!nowEditing);
  };

  useEffect(() => {
    if (!isEditing) setAutoRotate(true);
  }, [isEditing]);

  // Mirror front → back (optional)
  useEffect(() => {
    if (!mirrorBackFromFront) return;
    setBackTextContent(textContent);
    setBackTextColor(textColor);
    setBackTextPosition(textPosition);
    setBackTextScale(textScale);
    setBackTextFontSize(textFontSize);
  }, [
    mirrorBackFromFront,
    textContent, textColor, textFontSize,
    textPosition.x, textPosition.y, textScale.x, textScale.y
  ]);

  // Auto-fit (front text)
  useEffect(() => {
    if (!autoFitFront) return;
    const len = (textContent || "").length;
    const sx = Math.max(0.16, 0.28 - Math.max(0, len - 8) * 0.012);
    const sy = Math.min(0.28, Math.max(0.18, sx));
    setTextScale({ x: Number(sx.toFixed(3)), y: Number(sy.toFixed(3)) });
  }, [textContent, autoFitFront]);
  // Auto-fit (back name)
  useEffect(() => {
    if (!autoFitBack) return;
    const len = (backTextContent || "").length;
    const sx = Math.max(0.16, 0.28 - Math.max(0, len - 8) * 0.012);
    const sy = Math.min(0.28, Math.max(0.18, sx));
    setBackTextScale({ x: Number(sx.toFixed(3)), y: Number(sy.toFixed(3)) });
  }, [backTextContent, autoFitBack]);

  // ---- Gradient UI helpers ----
  const addStop = () => {
    if (gradStops.length >= 8) return;
    const last = [...gradStops].sort((a,b)=>a.pos-b.pos).at(-1);
    const pos = Math.min(1, Math.max(0, (last?.pos ?? 1) - 0.1));
    setGradStops(prev => [...prev, makeStop("#ffffff", pos)]);
  };
  const removeStop = (id) => setGradStops(prev => prev.filter(s => s.id !== id));
  const updateStopColor = (id, color) => setGradStops(prev => prev.map(s => s.id === id ? { ...s, color } : s));
  const updateStopPos   = (id, pos)   => setGradStops(prev => prev.map(s => s.id === id ? { ...s, pos: Math.max(0, Math.min(1, pos)) } : s));
  const sortedStops = useMemo(() => [...gradStops].sort((a,b)=>a.pos-b.pos), [gradStops]);

  // -------------------- Uploaded Logo Handlers --------------------
  const acceptTypes = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
  const MAX_MB = 10;

  function validateFile(file) {
    if (!file) return "File missing.";
    if (!acceptTypes.includes(file.type)) return "Invalid file type. Use PNG, JPG, SVG or WebP.";
    if (file.size > MAX_MB * 1024 * 1024) return `File too large. Max ${MAX_MB} MB.`;
    return null;
    }

  // Clean up blob when URL changes/unmounts
  useEffect(() => {
    return () => {
      if (userLogo1Ref.current) URL.revokeObjectURL(userLogo1Ref.current);
      if (userLogo2Ref.current) URL.revokeObjectURL(userLogo2Ref.current);
    };
  }, []);

  const handleUploadLogo1 = (file) => {
    const err = validateFile(file);
    if (err) { alert(err); return; }
    const blobUrl = URL.createObjectURL(file);
    if (userLogo1Ref.current) URL.revokeObjectURL(userLogo1Ref.current);
    userLogo1Ref.current = blobUrl;
    setUserLogo1Url(blobUrl);
    setSelectedLogo({ id: "custom-1", name: file.name || "Custom Logo 1", img: blobUrl });
    setShowLogo(true);
    setActiveLogoPanel("logo1");
  };

  const handleUploadLogo2 = (file) => {
    const err = validateFile(file);
    if (err) { alert(err); return; }
    const blobUrl = URL.createObjectURL(file);
    if (userLogo2Ref.current) URL.revokeObjectURL(userLogo2Ref.current);
    userLogo2Ref.current = blobUrl;
    setUserLogo2Url(blobUrl);
    setSelectedLogo2({ id: "custom-2", name: file.name || "Custom Logo 2", img: blobUrl });
    setShowLogo2(true);
    setActiveLogoPanel("logo2");
  };

  const clearUpload1 = () => {
    if (userLogo1Ref.current) URL.revokeObjectURL(userLogo1Ref.current);
    userLogo1Ref.current = null;
    setUserLogo1Url(null);
    setSelectedLogo(LOGOS[0] ?? null);
  };
  const clearUpload2 = () => {
    if (userLogo2Ref.current) URL.revokeObjectURL(userLogo2Ref.current);
    userLogo2Ref.current = null;
    setUserLogo2Url(null);
    setSelectedLogo2(LOGOS[0] ?? null);
  };

  // Hidden inputs for uploads (so we can trigger with buttons/cards)
  const fileInput1Ref = useRef(null);
  const fileInput2Ref = useRef(null);

  // -------------------- Tabs UI renderers --------------------
  const renderPatternTab = () => (
    <div className="designs">
      <div className="designs-head">
        <button className={`seg ${!showPattern ? "active" : ""}`} onClick={() => setShowPattern(false)}>No Pattern</button>
        <button className={`seg ${showPattern ? "active" : ""}`} onClick={() => setShowPattern(true)}>With Pattern</button>
      </div>

      {showPattern && (
        <>
          <div className="grid-cards">
            {PATTERNS.map((pattern) => (
              <button
                key={pattern.id}
                className={`card ${selectedPattern?.id === pattern.id ? "sel" : ""}`}
                onClick={() => { setSelectedPattern(pattern); setShowPattern(true); }}
                title={pattern.name}
              >
                <img src={pattern.img} alt={pattern.name} />
              </button>
            ))}
          </div>

          <div className="color-drawer" style={{ marginTop: 12 }}>
            <h4>Pattern Options</h4>
            <div className="grid2">
              <label>Repeat X</label>
              <input type="number" step="0.1" min="0.2"
                     value={patternRepeat.x}
                     onChange={(e) => setPatternRepeat((r) => ({ ...r, x: parseFloat(e.target.value || "1") }))}/>
              <label>Repeat Y</label>
              <input type="number" step="0.1" min="0.2"
                     value={patternRepeat.y}
                     onChange={(e) => setPatternRepeat((r) => ({ ...r, y: parseFloat(e.target.value || "1") }))}/>
              <label>Strength</label>
              <input type="range" min="0" max="1" step="0.01"
                     value={patternStrength}
                     onChange={(e) => setPatternStrength(parseFloat(e.target.value))}/>
              <label>Pattern Color</label>
              <input type="color" value={patternColor} onChange={(e)=>setPatternColor(e.target.value)} />
            </div>
            <small className="hint">Strength 0 → base visible, 1 → pattern tint strong. Repeat = tile scale.</small>
          </div>
        </>
      )}
    </div>
  );

  const renderColorDrawer = () => (
    <div className="color-drawer">
      <h4>Colors</h4>
      <div className="grid2">
        <label>Front Shirt</label><input type="color" value={tintFront} onChange={(e)=>setTintFront(e.target.value)} />
        <label>Back Shirt</label><input type="color" value={tintBack} onChange={(e)=>setTintBack(e.target.value)} />
       
        <label>L Arm Stripe</label><input type="color" value={tintLArmStripe} onChange={(e)=>setTintLArmStripe(e.target.value)} />
        <label>R Arm Stripe</label><input type="color" value={tintRArmStripe} onChange={(e)=>setTintRArmStripe(e.target.value)} />
        <label>L Shoulder</label><input type="color" value={tintLShoulder} onChange={(e)=>setTintLShoulder(e.target.value)} />
        <label>R Shoulder</label><input type="color" value={tintRShoulder} onChange={(e)=>setTintRShoulder(e.target.value)} />
        <label>Neck / Collar</label><input type="color" value={tintNeck} onChange={(e)=>setTintNeck(e.target.value)} />
        <label>Shorts</label><input type="color" value={tintShorts} onChange={(e)=>setTintShorts(e.target.value)} />
      </div>
      <small className="hint">White = disable tint (pattern/logo/text visible as-is).</small>
    </div>
  );

  const renderGradientTab = () => (
    <div className="designs">
      <div className="color-drawer">
        <h4>Model Gradient (multi-color)</h4>

        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={useBaseGrad} onChange={(e) => setUseBaseGrad(e.target.checked)} />
          Apply gradient on model
        </label>

        <div className="grid2" style={{ marginTop: 8 }}>
          <label>Angle (°)</label>
          <input type="number" min="0" max="360" value={baseGradAngleDeg} onChange={(e)=>setBaseGradAngleDeg(parseFloat(e.target.value || "0"))} />
        </div>

        <div className="stops" style={{ marginTop: 12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <h5 style={{ margin:0 }}>Color Stops</h5>
            <button type="button" onClick={addStop} disabled={gradStops.length >= 8} style={{ width: "6rem", height: "2.5rem" , borderRadius: "8px" }}>+ Add stop</button>
          </div>

          {sortedStops.map((s, idx) => (
            <div key={s.id} className="stop-row" style={{ display:"grid", gridTemplateColumns:"auto 120px 1fr auto", gap:8, alignItems:"center", marginBottom:8 }}>
              <span style={{ fontSize:12 }}>#{idx+1}</span>
              <input type="color" value={s.color} onChange={(e)=>updateStopColor(s.id, e.target.value)} />
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <input type="range" min="0" max="1" step="0.01" value={s.pos} onChange={(e)=>updateStopPos(s.id, parseFloat(e.target.value))} style={{ width:"100%" }} />
                <input type="number" min="0" max="1" step="0.01" value={s.pos} onChange={(e)=>updateStopPos(s.id, parseFloat(e.target.value || "0"))} style={{ width:70 }} />
              </div>
              <button type="button" onClick={()=>removeStop(s.id)} disabled={gradStops.length<=1}>✕</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ---------- LOGO TAB (includes upload for both logos) ----------
  const renderLogoTab = () => (
    <div className="designs">
      {/* LOGO 1 (SHIRT) */}
      <div className="designs-head">
        <button className={`seg ${!showLogo ? "active" : ""}`} onClick={() => { setShowLogo(false); setActiveLogoPanel(null); }}>No Shirt Logo</button>
        <button
          className={`seg ${showLogo ? "active" : ""}`}
          onClick={() => {
            setShowLogo(true);
            setActiveLogoPanel("logo1");
            if (!selectedLogo) setSelectedLogo(LOGOS[0]);
          }}
        >
          With Shirt Logo
        </button>
      </div>

      {showLogo && (
        <>
          <div className="grid-cards">
            {/* Upload card */}
            <button
              className="card upload"
              onClick={() => fileInput1Ref.current?.click()}
              title="Upload your logo"
              style={{ borderStyle: "dashed", borderColor: "#999" }}
            >
              <div style={{ padding: 10, fontSize: 13 }}>
                <div style={{ fontSize: 24, lineHeight: 1 }}>⬆️</div>
                <div>Upload…</div>
                <small>(PNG/JPG/SVG/WebP)</small>
              </div>
            </button>

            {/* Built-ins */}
            {LOGOS.map((logo) => (
              <button
                key={logo.id}
                className={`card ${selectedLogo?.id === logo.id ? "sel" : ""}`}
                onClick={() => { setSelectedLogo(logo); setActiveLogoPanel("logo1"); }}
                title={logo.name}
              >
                <img src={logo.img} alt={logo.name} />
              </button>
            ))}

            {/* Uploaded preview (if any) */}
            {userLogo1Url && (
              <button
                className={`card ${selectedLogo?.id === "custom-1" ? "sel" : ""}`}
                onClick={() => { setSelectedLogo({ id: "custom-1", name: "Custom Logo 1", img: userLogo1Url }); setActiveLogoPanel("logo1"); }}
                title="Custom Logo 1"
              >
                <img src={userLogo1Url} alt="Custom Logo 1" />
              </button>
            )}
          </div>

          {/* Hidden input */}
          <input
            ref={fileInput1Ref}
            type="file"
            accept={acceptTypes.join(",")}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUploadLogo1(file);
              e.target.value = "";
            }}
          />

          {(activeLogoPanel === "logo1") && (
            <div className="color-drawer" style={{ marginTop: 12 }}>
              <h4>Shirt Logo Options</h4>
              <div className="grid2">
                <label>Position X</label>
                <input type="range" min="0" max="1" step="0.01" value={logoPosition.x} onChange={(e) => setLogoPosition((p) => ({ ...p, x: parseFloat(e.target.value) }))} />
                <label>Position Y</label>
                <input type="range" min="0" max="1" step="0.01" value={logoPosition.y} onChange={(e) => setLogoPosition((p) => ({ ...p, y: parseFloat(e.target.value) }))} />

                {/* Single scale for X & Y */}
                <label>Scale</label>
                <input
                  type="range" min="0.02" max="1" step="0.005"
                  value={logoScale.x}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setLogoScale({ x: v, y: v });
                  }}
                />
                <label>Logo Color</label>
                <input type="color" value={logoColor} onChange={(e) => setLogoColor(e.target.value)} />

                {/* Upload & clear controls */}
                <label>Upload</label>
                <div>
                  <button type="button" onClick={() => fileInput1Ref.current?.click()}>Choose file…</button>
                  {userLogo1Url && <button type="button" style={{ marginLeft: 8 }} onClick={clearUpload1}>Remove custom</button>}
                </div>
              </div>
              <small className="hint">Shirt logo ab sirf front par aayega.</small>
            </div>
          )}
        </>
      )}

      {/* --------- LOGO 2 (SHORTS) ---------- */}
      <div style={{ borderTop: "1px solid #ddd", paddingTop: 10, marginTop: 10 }}>
        <div className="designs-head">
          <button className={`seg ${!showLogo2 ? "active" : ""}`} onClick={() => { setShowLogo2(false); if(activeLogoPanel==="logo2") setActiveLogoPanel(null); }}>No Shorts Logo</button>
          <button
            className={`seg ${showLogo2 ? "active" : ""}`}
            onClick={() => {
              setShowLogo2(true);
              setActiveLogoPanel("logo2");
              if (!selectedLogo2) setSelectedLogo2(LOGOS[0]);
            }}
          >
            With Shorts Logo
          </button>
        </div>

        {showLogo2 && (
          <>
            <div className="grid-cards">
              {/* Upload card */}
              <button
                className="card upload"
                onClick={() => fileInput2Ref.current?.click()}
                title="Upload your logo"
                style={{ borderStyle: "dashed", borderColor: "#999" }}
              >
                <div style={{ padding: 10, fontSize: 13 }}>
                  <div style={{ fontSize: 24, lineHeight: 1 }}>⬆️</div>
                  <div>Upload…</div>
                  <small>(PNG/JPG/SVG/WebP)</small>
                </div>
              </button>

              {/* Built-ins */}
              {LOGOS.map((logo) => (
                <button
                  key={logo.id}
                  className={`card ${selectedLogo2?.id === logo.id ? "sel" : ""}`}
                  onClick={() => { setSelectedLogo2(logo); setActiveLogoPanel("logo2"); }}
                  title={logo.name}
                >
                  <img src={logo.img} alt={logo.name} />
                </button>
              ))}

              {/* Uploaded preview (if any) */}
              {userLogo2Url && (
                <button
                  className={`card ${selectedLogo2?.id === "custom-2" ? "sel" : ""}`}
                  onClick={() => { setSelectedLogo2({ id: "custom-2", name: "Custom Logo 2", img: userLogo2Url }); setActiveLogoPanel("logo2"); }}
                  title="Custom Logo 2"
                >
                  <img src={userLogo2Url} alt="Custom Logo 2" />
                </button>
              )}
            </div>

            {/* Hidden input */}
            <input
              ref={fileInput2Ref}
              type="file"
              accept={acceptTypes.join(",")}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadLogo2(file);
                e.target.value = "";
              }}
            />

            {(activeLogoPanel === "logo2") && (
              <div className="color-drawer" style={{ marginTop: 12 }}>
                <h4>Shorts Logo Options</h4>
                <div className="grid2">
                  <label>Position X</label>
                  <input type="range" min="0" max="1" step="0.01" value={logo2Position.x} onChange={(e) => setLogo2Position((p) => ({ ...p, x: parseFloat(e.target.value) }))} />
                  <label>Position Y</label>
                  <input type="range" min="0" max="1" step="0.01" value={logo2Position.y} onChange={(e) => setLogo2Position((p) => ({ ...p, y: parseFloat(e.target.value) }))} />

                  {/* Single scale for X & Y */}
                  <label>Scale</label>
                  <input
                    type="range" min="0.02" max="1" step="0.005"
                    value={logo2Scale.x}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      setLogo2Scale({ x: v, y: v });
                    }}
                  />
                  <label>Logo 2 Color</label>
                  <input type="color" value={logo2Color} onChange={(e) => setLogo2Color(e.target.value)} />

                  {/* Upload & clear controls */}
                  <label>Upload</label>
                  <div>
                    <button type="button" onClick={() => fileInput2Ref.current?.click()}>Choose file…</button>
                    {userLogo2Url && <button type="button" style={{ marginLeft: 8 }} onClick={clearUpload2}>Remove custom</button>}
                  </div>
                </div>
                <small className="hint">Shorts logo same as before (agar chaho to shorts ka mirror bhi hata sakte hain).</small>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderTextTab = () => (
    <div className="designs">
      <div className="designs-head">
        <button className={`seg ${!showText ? "active" : ""}`} onClick={() => setShowText(false)}>No Text</button>
        <button className={`seg ${showText ? "active" : ""}`} onClick={() => setShowText(true)}>With Text</button>
      </div>

      {showText && (
        <>
          <div className="color-drawer" style={{ marginTop: 12 }}>
            <h4>Front Text Options</h4>
            <div className="grid2" style={{ marginBottom: 12 }}>
              <label>Your Text</label>
              <input type="text" value={textContent} onChange={(e) => setTextContent(e.target.value)} placeholder="Enter your text here"
                     style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}/>
            </div>

            <div className="grid2">
              <label>Position X</label>
              <input type="range" min="0" max="1" step="0.01" value={textPosition.x} onChange={(e) => setTextPosition((p) => ({ ...p, x: parseFloat(e.target.value) }))} />
              <label>Position Y</label>
              <input type="range" min="0" max="1" step="0.01" value={textPosition.y} onChange={(e) => setTextPosition((p) => ({ ...p, y: parseFloat(e.target.value) }))} />
              <label>Scale X</label>
              <input type="range" min="0.12" max="0.5" step="0.005" value={textScale.x} onChange={(e) => setTextScale((s) => ({ ...s, x: parseFloat(e.target.value) }))} />
              <label>Scale Y</label>
              <input type="range" min="0.12" max="0.5" step="0.005" value={textScale.y} onChange={(e) => setTextScale((s) => ({ ...s, y: parseFloat(e.target.value) }))} />
              <label>Font Size (px)</label>
              <input type="number" min="48" max="220" step="1" value={textFontSize} onChange={(e)=>setTextFontSize(parseInt(e.target.value || "80", 10))} />
              <label>Text Color</label>
              <input type="color" value={textColor} onChange={(e)=>setTextColor(e.target.value)} />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderNameTab = () => (
    <div className="designs">
      <div className="designs-head">
        <button className={`seg ${!showBackText ? "active" : ""}`} onClick={() => setShowBackText(false)}>No Name</button>
        <button className={`seg ${showBackText ? "active" : ""}`} onClick={() => setShowBackText(true)}>With Name</button>
      </div>

      {showBackText && (
        <>
          <div className="color-drawer" style={{ marginTop: 12 }}>
            <h4>Back Name Options</h4>

            <div className="grid2" style={{ marginBottom: 12 }}>
              <label>Player Name</label>
              <input type="text" value={backTextContent} onChange={(e) => setBackTextContent(e.target.value)} placeholder="Enter player name here"
                     style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}/>
            </div>

            <div className="grid2" style={{ marginBottom: 12 }}>
              <label>
                <input type="checkbox" checked={mirrorBackFromFront} onChange={(e)=>setMirrorBackFromFront(e.target.checked)} style={{ marginRight: 8 }}/>
                Same as front (auto-mirror)
              </label>
              <button type="button" onClick={()=>{
                setBackTextContent(textContent);
                setBackTextColor(textColor);
                setBackTextPosition(textPosition);
                setBackTextScale(textScale);
                setBackTextFontSize(textFontSize);
              }}>
                Copy current front → back (once)
              </button>
            </div>

            <div className="grid2">
              <label>Position X</label>
              <input type="range" min="0" max="1" step="0.01" value={backTextPosition.x} onChange={(e) => setBackTextPosition((p) => ({ ...p, x: parseFloat(e.target.value) }))} />
              <label>Position Y</label>
              <input type="range" min="0" max="1" step="0.01" value={backTextPosition.y} onChange={(e) => setBackTextPosition((p) => ({ ...p, y: parseFloat(e.target.value) }))} />
              <label>Scale X</label>
              <input type="range" min="0.12" max="0.5" step="0.005" value={backTextScale.x} onChange={(e) => setBackTextScale((s) => ({ ...s, x: parseFloat(e.target.value) }))} />
              <label>Scale Y</label>
              <input type="range" min="0.12" max="0.5" step="0.005" value={backTextScale.y} onChange={(e) => setBackTextScale((s) => ({ ...s, y: parseFloat(e.target.value) }))} />
              <label>Font Size (px)</label>
              <input type="number" min="48" max="220" step="1" value={backTextFontSize} onChange={(e)=>setBackTextFontSize(parseInt(e.target.value || "80", 10))} />
              <label>Name Color</label>
              <input type="color" value={backTextColor} onChange={(e) => setBackTextColor(e.target.value)} />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderNumberTab = () => (
    <div className="designs">
      <div className="color-drawer">
        <h4>Numbers (Back Only)</h4>

        <div style={{ borderTop: "1px solid #ddd", paddingTop: 10, marginTop: 10 }}>
          <div className="designs-head" style={{ marginBottom: 8 }}>
            <button className={`seg ${!showBackNumber ? "active" : ""}`} onClick={() => setShowBackNumber(false)}>Disable #B</button>
            <button className={`seg ${showBackNumber ? "active" : ""}`} onClick={() => setShowBackNumber(true)}>Enable #B</button>
          </div>
          {showBackNumber && (
            <div className="grid2">
              <label>Number B</label>
              <input type="text" value={backNumberValue}
                     onChange={(e)=>setBackNumberValue(e.target.value.replace(/[^0-9]/g,"").slice(0,3))}
                     placeholder="e.g., 9 or 23 or 101"
                     style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}/>
              <label>Position X</label>
              <input type="range" min="0" max="1" step="0.01" value={backNumberPosition.x} onChange={(e)=>setBackNumberPosition(p=>({...p, x: parseFloat(e.target.value)}))} />
              <label>Position Y</label>
              <input type="range" min="0" max="1" step="0.01" value={backNumberPosition.y} onChange={(e)=>setBackNumberPosition(p=>({...p, y: parseFloat(e.target.value)}))} />
              <label>Scale X</label>
              <input type="range" min="0.1" max="1" step="0.01" value={backNumberScale.x} onChange={(e)=>setBackNumberScale(s=>({...s, x: parseFloat(e.target.value)}))} />
              <label>Scale Y</label>
              <input type="range" min="0.1" max="1" step="0.01" value={backNumberScale.y} onChange={(e)=>setBackNumberScale(s=>({...s, y: parseFloat(e.target.value)}))} />
              <label>Font Size (px)</label>
              <input type="number" min="80" max="360" step="1" value={backNumberFontSize} onChange={(e)=>setBackNumberFontSize(parseInt(e.target.value||"190",10))} />
              <label>Color</label>
              <input type="color" value={backNumberColor} onChange={(e)=>setBackNumberColor(e.target.value)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="cfg-wrap">
      <div className="cfg-body">
        {/* -------------------- LEFT PANEL -------------------- */}
        <aside className="cfg-left">
          <div className="l-tabs">
            {["Design", "Color", "Pattern", "Gradient", "Number", "Name", "Text", "Logo"].map((t) => (
              <button key={t} className={`l-tab ${activeTab === t ? "active" : ""}`} onClick={() => handleTabChange(t)}>
                {t}
              </button>
            ))}
          </div>

          {activeTab === "Design" ? (
            <div className="designs">
              <div className="designs-head">
                <button className="seg active">Designs</button>
                <button className="seg">Best customer designs</button>
              </div>
              <div className="grid-cards" style={{ backgroundSize: "cover", backgroundImage: previewStyle.backgroundImage }}>
                {ONLINE_DESIGNS.map((d) => (
                  <button key={d.id} className={`card ${selected.id === d.id ? "sel" : ""}`} onClick={() => setSelected(d)} title={d.name}>
                    <img src={d.img} alt={d.name} />
                  </button>
                ))}
              </div>
              <button className="add-product">+ Add product</button>
            </div>
          ) : activeTab === "Color" ? (
            renderColorDrawer()
          ) : activeTab === "Pattern" ? (
            renderPatternTab()
          ) : activeTab === "Gradient" ? (
            renderGradientTab()
          ) : activeTab === "Logo" ? (
            renderLogoTab()
          ) : activeTab === "Text" ? (
            renderTextTab()
          ) : activeTab === "Name" ? (
            renderNameTab()
          ) : activeTab === "Number" ? (
            renderNumberTab()
          ) : (
            <div className="placeholder">
              <p>Panel for <b>{activeTab}</b> (UI only).</p>
            </div>
          )}
        </aside>

        {/* -------------------- RIGHT PANEL (3D MODEL) -------------------- */}
        <main className="cfg-stage">
          <div className="stage-inner">
            <Canvas camera={{ position: [0, 0, 2.2], fov: 45 }}>
              <ambientLight intensity={0.6} />
              <directionalLight position={[5, 5, 5]} intensity={1.2} />

              <TexturedSoccerKit
                // Base fallback
                baseColor={bgColor}

                // Gradient
                useBaseGrad={useBaseGrad}
                gradStops={sortedStops}
                gradAngle={baseGradAngleDeg * Math.PI / 180}

                // Pattern
                patternUrl={selectedPattern?.img}
                showPattern={!!(showPattern && selectedPattern)}
                patternColor={patternColor}
                patternRepeat={[patternRepeat.x, patternRepeat.y]}
                patternStrength={patternStrength}

                // Logo 1 (Shirt)
                logoUrl={(selectedLogo?.id === "custom-1" && userLogo1Url) ? userLogo1Url : selectedLogo?.img}
                showLogo={!!(showLogo && (selectedLogo?.img || userLogo1Url))}
                logoColor={logoColor}
                logoPosition={[logoPosition.x, logoPosition.y]}
                logoScale={[logoScale.x, logoScale.y]}

                // Logo 2 (Shorts)
                logo2Url={(selectedLogo2?.id === "custom-2" && userLogo2Url) ? userLogo2Url : selectedLogo2?.img}
                showLogo2={!!(showLogo2 && (selectedLogo2?.img || userLogo2Url))}
                logo2Color={logo2Color}
                logo2Position={[logo2Position.x, logo2Position.y]}
                logo2Scale={[logo2Scale.x, logo2Scale.y]}

                // Front Text (FRONT ONLY)
                textContent={textContent}
                showText={!!(showText && textContent)}
                textColor={textColor}
                textPosition={[textPosition.x, textPosition.y]}
                textScale={[textScale.x, textScale.y]}
                textFontSize={textFontSize}

                // Back Text (Name) (BACK ONLY)
                backTextContent={backTextContent}
                showBackText={!!(showBackText && backTextContent)}
                backTextColor={backTextColor}
                backTextPosition={[backTextPosition.x, backTextPosition.y]}
                backTextScale={[backTextScale.x, backTextScale.y]}
                backTextFontSize={backTextFontSize}

                // Numbers (BACK ONLY)
                frontNumberValue={frontNumberValue}
                showFrontNumber={!!(showFrontNumber && frontNumberValue)}
                frontNumberColor={frontNumberColor}
                frontNumberPosition={[frontNumberPosition.x, frontNumberPosition.y]}
                frontNumberScale={[frontNumberScale.x, frontNumberScale.y]}
                frontNumberFontSize={frontNumberFontSize}

                backNumberValue={backNumberValue}
                showBackNumber={!!(showBackNumber && backNumberValue)}
                backNumberColor={backNumberColor}
                backNumberPosition={[backNumberPosition.x, backNumberPosition.y]}
                backNumberScale={[backNumberScale.x, backNumberScale.y]}
                backNumberFontSize={backNumberFontSize}

                // Per-part tints - INCLUDING SLEEVES AND SHORTS
                tintFront={tintFront}
                tintBack={tintBack}
                tintLArmStripe={tintLArmStripe}
                tintRArmStripe={tintRArmStripe}
                tintLShoulder={tintLShoulder}
                tintRShoulder={tintRShoulder}
                tintNeck={tintNeck}
                tintLSleeve={tintLSleeve}
                tintRSleeve={tintRSleeve}
                tintShorts={tintShorts}
              />

              <OrbitControls
                ref={controlsRef}
                enableZoom={false}
                enablePan={false}
                enableRotate={true}
                minPolarAngle={Math.PI / 2}
                maxPolarAngle={Math.PI / 2}
                autoRotate={autoRotate}
                autoRotateSpeed={1.2}
                target={[0, 0, 0]}
              />
            </Canvas>
          </div>

          <div className="cfg-right">
            <button className="tool" title={autoRotate ? "Pause Rotation" : "Start Rotation"} onClick={() => setAutoRotate(!autoRotate)}>
              {autoRotate ? "⏸️" : "▶️"}
            </button>
            <button className="tool" title="Zoom In">＋</button>
            <button className="tool" title="Zoom Out">－</button>
          </div>
        </main>
      </div>
    </div>
  );
}

// -------------------- PRELOAD MODEL --------------------
useGLTF.preload("/assets/images/Full Soccer Kit (1).glb");
